import { useFlag } from '../lib/useFlag';
import { Navigate } from 'react-router-dom';
import { ReactNode } from 'react';
import { usePermissions } from '../lib/usePermissions';
import type { PermissionAction } from '../lib/usePermissions';

/**
 * Route guards — the enforcement half of the permission model in the shell.
 *
 * The backend already default-denies every object per-route; these guards keep a
 * user from even mounting a standalone browse page they can't use (so a deep link
 * to a super_admin-only surface, or a fee/deal surface without the grant, shows a
 * clean "Access restricted" panel instead of a page that 403s every query).
 *
 * Default-DENY while loading: guards render nothing until the permission grid
 * resolves, so the gated content never flashes before the decision is known.
 */

function AccessRestricted({ message }: { message: string }) {
  return (
    <div style={{
      padding: 'var(--sp-5)',
      background: 'var(--color-status-red-bg)',
      border: '1px solid var(--color-status-red)',
      color: 'var(--color-status-red)',
      borderRadius: 'var(--r-lg)',
    }}>
      <h2 style={{ margin: '0 0 var(--sp-2)' }}>Access restricted</h2>
      <p style={{ margin: 0 }}>{message}</p>
    </div>
  );
}

/** Gate a route to super_admin only (Org Settings, Audit Log, User Management). */
export function RequireSuperAdmin({ children }: { children: ReactNode }) {
  const { data, isLoading } = usePermissions();
  if (isLoading) return null; // still resolving — nothing to show yet
  // Fail CLOSED on a transient permissions-fetch error (not loading, no data):
  // show the restricted panel, never a blank area.
  if (!data) return <AccessRestricted message="Couldn't verify your permissions — please reload." />;
  if (data.user.role !== 'super_admin') {
    return <AccessRestricted message="This area is available to super admins only." />;
  }
  return <>{children}</>;
}

/**
 * Gate a route on holding `action` (default can_view) on `entity`. super_admin
 * always passes (reflection returns all-true); everyone else needs the grant —
 * so the fee/deal surfaces stay hidden by default yet become reachable the moment
 * a super_admin grants them through the permissions matrix.
 */
export function RequireEntity({
  entity,
  action = 'can_view',
  children,
}: {
  entity: string;
  action?: PermissionAction;
  children: ReactNode;
}) {
  const { data, isLoading } = usePermissions();
  if (isLoading) return null; // still resolving — nothing to show yet
  if (!data) return <AccessRestricted message="Couldn't verify your permissions — please reload." />;
  const allowed = data.user.role === 'super_admin' || data.permissions[entity]?.[action] === true;
  if (!allowed) {
    return <AccessRestricted message="You don't have permission to view this area. Ask a super admin to grant it." />;
  }
  return <>{children}</>;
}

/**
 * UnlessFlag — render `children` unless the org has the named feature flag ON, in which case
 * send the visitor to `to`. The inverse of a feature gate: a flag that HIDES a concept (the
 * Parent Clients toggle) keeps the routes wired for every other org while a deep link into
 * the hidden surface lands somewhere sensible instead of on a page the nav no longer offers.
 */
export function UnlessFlag({ flag, to, children }: { flag: string; to: string; children: ReactNode }) {
  const on = useFlag(flag);
  if (on) return <Navigate to={to} replace />;
  return <>{children}</>;
}
